import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { heuteIso, fmtDatum, fmtEuro } from '../components/ZeitraumWahl';

// Rollen des Teams mit Stundensätzen. Sätze gelten ab einem Datum und wirken
// nie rückwirkend – wie die Bahnpreise.
const LEER = { name: '', beschreibung: '', reihenfolge: 0, aktiv: true };

function RolleDialog({ rolle, abgeschlossenBis, onClose, onGespeichert }) {
  const [form, setForm] = useState(() => (rolle
    ? { ...LEER, ...rolle, beschreibung: rolle.beschreibung || '' }
    : LEER));
  const [satz, setSatz] = useState({ satz_pro_stunde: '', gueltig_ab: heuteIso() });
  const [fehler, setFehler] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };

  const absenden = async () => {
    if (!form.name.trim()) return setFehler('Bitte einen Namen für die Rolle angeben');
    setSpeichert(true); setFehler('');
    try {
      if (rolle) await api.put(`/api/admin/rollen/${rolle.id}`, form);
      else await api.post('/api/admin/rollen', form);
      onGespeichert();
    } catch (err) { setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };

  const loeschen = async () => {
    if (!window.confirm(`Rolle „${rolle.name}" wirklich löschen?`)) return;
    try { await api.delete(`/api/admin/rollen/${rolle.id}`); onGespeichert(); }
    catch (err) { setFehler(err.response?.data?.error || 'Löschen fehlgeschlagen'); }
  };

  const satzAnlegen = async () => {
    if (!String(satz.satz_pro_stunde).trim()) return setFehler('Bitte einen Stundensatz angeben');
    setFehler('');
    try {
      await api.post(`/api/admin/rollen/${rolle.id}/saetze`, satz);
      setSatz({ satz_pro_stunde: '', gueltig_ab: heuteIso() });
      onGespeichert(true);
    } catch (err) { setFehler(err.response?.data?.error || 'Satz konnte nicht gespeichert werden'); }
  };

  const satzLoeschen = async (s) => {
    if (!window.confirm(`Satz ${fmtEuro(s.satz_pro_stunde)} ab ${fmtDatum(s.gueltig_ab)} löschen?`)) return;
    try { await api.delete(`/api/admin/rollen/saetze/${s.id}`); onGespeichert(true); }
    catch (err) { setFehler(err.response?.data?.error || 'Löschen fehlgeschlagen'); }
  };

  return (
    <Modal title={rolle ? rolle.name : 'Rolle hinzufügen'} onClose={onClose} size="md">
      <div className="form-group"><label className="form-label">Name der Rolle *</label>
        <input className="form-control" value={form.name} maxLength={60} autoFocus
          placeholder="z.B. Schwimmsporthelfer" onChange={e => set('name', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Beschreibung</label>
        <input className="form-control" value={form.beschreibung} maxLength={200}
          placeholder="optional, z.B. Qualifikation oder Hinweis" onChange={e => set('beschreibung', e.target.value)} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem', alignItems: 'end' }}>
        <div className="form-group"><label className="form-label">Reihenfolge</label>
          <input type="number" className="form-control" value={form.reihenfolge}
            onChange={e => set('reihenfolge', e.target.value)} /></div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', marginBottom: '1rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.aktiv} onChange={e => set('aktiv', e.target.checked)} /> aktiv
        </label>
      </div>

      {rolle && (
        <div className="form-group">
          <label className="form-label">Stundensätze</label>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Jeder Satz gilt ab seinem Datum. Gerechnet wird mit dem Satz, der am Tag des Einsatzes galt –
            eine Änderung verändert also keine bereits berechneten Beträge.
          </div>
          <div className="table-wrap" style={{ marginBottom: '0.6rem' }}>
            <table>
              <thead><tr><th>Gültig ab</th><th>Satz je Stunde</th><th>Erfasst von</th><th></th></tr></thead>
              <tbody>
                {rolle.saetze.length === 0 && (
                  <tr><td colSpan={4} style={{ color: '#c62828', padding: '0.75rem' }}>
                    Noch kein Satz hinterlegt – Stunden dieser Rolle ergeben 0,00 €.
                  </td></tr>
                )}
                {rolle.saetze.map(s => {
                  const gesperrt = abgeschlossenBis && s.gueltig_ab <= abgeschlossenBis;
                  return (
                    <tr key={s.id}>
                      <td>{fmtDatum(s.gueltig_ab)}</td>
                      <td style={{ fontWeight: 700 }}>{fmtEuro(s.satz_pro_stunde)}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{s.erfasst_von_name || '–'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {gesperrt
                          ? <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>🔒 abgeschlossen</span>
                          : <button className="btn btn-danger btn-sm" style={{ padding: '1px 8px' }} onClick={() => satzLoeschen(s)}>✕</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 130px' }}>
              <label className="form-label">Neuer Satz (€)</label>
              <input className="form-control" inputMode="decimal" placeholder="z.B. 12,50"
                value={satz.satz_pro_stunde} onChange={e => setSatz(p => ({ ...p, satz_pro_stunde: e.target.value }))} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label className="form-label">Gültig ab</label>
              <input type="date" className="form-control" value={satz.gueltig_ab}
                onChange={e => setSatz(p => ({ ...p, gueltig_ab: e.target.value }))} />
            </div>
            <button className="btn btn-secondary btn-sm" onClick={satzAnlegen}>+ Satz</button>
          </div>
        </div>
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        {rolle && (rolle.personen === 0 && rolle.einsaetze === 0
          ? <button className="btn btn-danger btn-sm" onClick={loeschen}>Löschen</button>
          : <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {rolle.personen} Personen · {rolle.einsaetze} Einsätze – statt löschen „aktiv" abwählen
            </span>)}
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>Schließen</button>
        <button className="btn btn-primary btn-sm" onClick={absenden} disabled={speichert}>
          {speichert ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </Modal>
  );
}

export default function Rollen() {
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [dialog, setDialog] = useState(null); // null | 'neu' | rolle

  const load = useCallback(async (dialogOffenLassen = false) => {
    try {
      const r = await api.get('/api/admin/rollen');
      setDaten(r.data);
      setDialog(d => (dialogOffenLassen && d && d !== 'neu' ? r.data.rollen.find(x => x.id === d.id) || null : d));
    } catch { setFehler('Rollen konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rollen = daten?.rollen || [];
  const ohneSatz = rollen.filter(r => r.aktiv && !r.aktueller_satz);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Rollen &amp; Vergütung</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setDialog('neu')}>+ Rolle</button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Rollen für das Team – frei benennbar, zum Beispiel Helfer, Sporthelfer, Schwimmsporthelfer, Assistent,
        Schwimmlehrer oder Trainer. Der Stundensatz je Rolle ergibt zusammen mit den erfassten Stunden den
        auszuzahlenden Betrag unter „Trainer-/Helferstunden".
      </p>

      {fehler && <div className="alert alert-error">{fehler}</div>}
      {ohneSatz.length > 0 && (
        <div className="alert" style={{ background: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' }}>
          Ohne Stundensatz: <strong>{ohneSatz.map(r => r.name).join(', ')}</strong>.
          Stunden dieser Rollen werden mit 0,00 € gerechnet.
        </div>
      )}

      <div className="card">
        {laden ? <div style={{ padding: '2rem', textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Rolle</th><th>Satz je Stunde</th><th>Gültig ab</th><th>Frühere Sätze</th><th>Personen</th><th>Einsätze</th></tr></thead>
              <tbody>
                {rollen.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Noch keine Rollen</td></tr>
                )}
                {rollen.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer', opacity: r.aktiv ? 1 : 0.55 }} onClick={() => setDialog(r)}>
                    <td style={{ fontWeight: 600 }}>
                      {r.name}{!r.aktiv && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}> (inaktiv)</span>}
                      {r.beschreibung && <div className="td-muted" style={{ fontSize: '0.78rem' }}>{r.beschreibung}</div>}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      {r.aktueller_satz
                        ? fmtEuro(r.aktueller_satz.satz_pro_stunde)
                        : <span style={{ color: '#c62828', fontWeight: 600 }}>kein Satz</span>}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{r.aktueller_satz ? fmtDatum(r.aktueller_satz.gueltig_ab) : '–'}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {r.saetze.filter(s => s.id !== r.aktueller_satz?.id)
                        .map(s => `${fmtEuro(s.satz_pro_stunde)} ab ${fmtDatum(s.gueltig_ab)}`).join(' · ') || '–'}
                    </td>
                    <td>{r.personen_aktiv}{r.personen !== r.personen_aktiv ? ` (${r.personen})` : ''}</td>
                    <td>{r.einsaetze}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialog && (
        <RolleDialog rolle={dialog === 'neu' ? null : dialog} abgeschlossenBis={daten?.abgeschlossen_bis}
          onClose={() => setDialog(null)}
          onGespeichert={(offenLassen) => { if (!offenLassen) setDialog(null); load(offenLassen); }} />
      )}
    </div>
  );
}
